import React, { useState, useEffect, useCallback } from 'react';

const PersonalityAssessment = ({ onComplete, onBack }) => {
  // Big Five personality questions
  const personalityQuestions = [
    // Extraversion
    { id: 1, text: "I see myself as someone who is talkative", trait: "extraversion", reverse: false },
    { id: 6, text: "I see myself as someone who is reserved", trait: "extraversion", reverse: true },
    { id: 11, text: "I see myself as someone who is full of energy", trait: "extraversion", reverse: false },
    { id: 16, text: "I see myself as someone who generates a lot of enthusiasm", trait: "extraversion", reverse: false },
    { id: 21, text: "I see myself as someone who tends to be quiet", trait: "extraversion", reverse: true },
    
    // Agreeableness
    { id: 2, text: "I see myself as someone who tends to find fault with others", trait: "agreeableness", reverse: true },
    { id: 7, text: "I see myself as someone who is helpful and unselfish with others", trait: "agreeableness", reverse: false },
    { id: 12, text: "I see myself as someone who starts quarrels with others", trait: "agreeableness", reverse: true },
    { id: 17, text: "I see myself as someone who has a forgiving nature", trait: "agreeableness", reverse: false },
    { id: 22, text: "I see myself as someone who is generally trusting", trait: "agreeableness", reverse: false },
    
    // Conscientiousness
    { id: 3, text: "I see myself as someone who does a thorough job", trait: "conscientiousness", reverse: false },
    { id: 8, text: "I see myself as someone who can be somewhat careless", trait: "conscientiousness", reverse: true },
    { id: 13, text: "I see myself as someone who is a reliable worker", trait: "conscientiousness", reverse: false },
    { id: 18, text: "I see myself as someone who tends to be disorganized", trait: "conscientiousness", reverse: true },
    { id: 23, text: "I see myself as someone who tends to be lazy", trait: "conscientiousness", reverse: true },
    
    // Neuroticism
    { id: 4, text: "I see myself as someone who is depressed, blue", trait: "neuroticism", reverse: false },
    { id: 9, text: "I see myself as someone who is relaxed, handles stress well", trait: "neuroticism", reverse: true },
    { id: 14, text: "I see myself as someone who can be tense", trait: "neuroticism", reverse: false },
    { id: 19, text: "I see myself as someone who worries a lot", trait: "neuroticism", reverse: false },
    { id: 24, text: "I see myself as someone who is emotionally stable, not easily upset", trait: "neuroticism", reverse: true },
    
    // Openness
    { id: 5, text: "I see myself as someone who is original, comes up with new ideas", trait: "openness", reverse: false },
    { id: 10, text: "I see myself as someone who is curious about many different things", trait: "openness", reverse: false },
    { id: 15, text: "I see myself as someone who is ingenious, a deep thinker", trait: "openness", reverse: false },
    { id: 20, text: "I see myself as someone who has an active imagination", trait: "openness", reverse: false },
    { id: 25, text: "I see myself as someone who values artistic, aesthetic experiences", trait: "openness", reverse: false }
  ];

  // Social confidence questions
  const socialQuestions = [
    { id: 26, text: "How comfortable are you starting conversations with strangers?", trait: "social_confidence" },
    { id: 27, text: "How much do social interactions energize you?", trait: "social_energy" },
    { id: 28, text: "How well do you handle awkward silences in conversations?", trait: "conversation_recovery" },
    { id: 29, text: "How important is it for you to make others feel comfortable?", trait: "social_empathy" },
    { id: 30, text: "How confident do you feel in group social settings?", trait: "group_confidence" }
  ];

  const allQuestions = [...personalityQuestions, ...socialQuestions];
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [startTime] = useState(Date.now());

  const currentQuestion = allQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / allQuestions.length) * 100;
  const isLastQuestion = currentQuestionIndex === allQuestions.length - 1;

  const handleResponse = (value) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));

    if (isLastQuestion) {
      calculateAndSubmitResults();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const calculateTraitScore = useCallback((trait) => {
    const traitQuestions = personalityQuestions.filter(q => q.trait === trait);
    let total = 0;
    let count = 0;

    traitQuestions.forEach(question => {
      const response = responses[question.id];
      if (response !== undefined) {
        const score = question.reverse ? (6 - response) : response;
        total += score;
        count++;
      }
    });

    return count > 0 ? Math.round((total / count) * 20) : 50; // Convert to 0-100 scale
  }, [responses]);

  const calculateSocialMetrics = useCallback(() => {
    const metrics = {};
    socialQuestions.forEach(question => {
      const response = responses[question.id];
      if (response !== undefined) {
        metrics[question.trait] = response * 20; // Convert to 0-100 scale
      }
    });
    return metrics;
  }, [responses]);

  const calculateAndSubmitResults = () => {
    const duration = Date.now() - startTime;
    
    const bigFive = {
      openness: calculateTraitScore('openness'),
      conscientiousness: calculateTraitScore('conscientiousness'),
      extraversion: calculateTraitScore('extraversion'),
      agreeableness: calculateTraitScore('agreeableness'),
      neuroticism: calculateTraitScore('neuroticism')
    };

    const socialMetrics = calculateSocialMetrics();

    // Determine communication style based on personality
    const communicationStyle = determineCommuncationStyle(bigFive);
    
    // Calculate social confidence
    const socialConfidence = Math.round(
      (socialMetrics.social_confidence + socialMetrics.group_confidence) / 2
    );

    const profile = {
      bigFive,
      socialMetrics,
      communicationStyle,
      socialConfidence,
      assessmentDuration: duration,
      completedAt: new Date().toISOString(),
      reliability: calculateReliability()
    };

    onComplete(profile);
  };

  const determineCommuncationStyle = (bigFive) => {
    if (bigFive.openness > 70 && bigFive.conscientiousness > 70) {
      return 'intellectual';
    } else if (bigFive.extraversion > 70 && bigFive.agreeableness > 70) {
      return 'storytelling';
    } else if (bigFive.extraversion > 70 && bigFive.openness > 60) {
      return 'humor_based';
    } else if (bigFive.conscientiousness > 70) {
      return 'direct';
    } else {
      return 'casual';
    }
  };

  const calculateReliability = () => {
    // Check for response patterns that might indicate unreliable responses
    const responseValues = Object.values(responses);
    const uniqueValues = new Set(responseValues).size;
    
    // If all responses are the same, reliability is low
    if (uniqueValues === 1) return 0.3;
    
    // Check for alternating patterns
    const hasPattern = responseValues.every((val, i) => 
      i === 0 || Math.abs(val - responseValues[i - 1]) <= 1
    );
    
    if (hasPattern) return 0.7;
    return 0.9; // Good variety in responses
  };

  const answerOptions = currentQuestionIndex < 25 
    ? [
        { value: 1, label: "Strongly Disagree" },
        { value: 2, label: "Disagree" },
        { value: 3, label: "Neutral" },
        { value: 4, label: "Agree" },
        { value: 5, label: "Strongly Agree" }
      ]
    : [
        { value: 1, label: "Not at all" },
        { value: 2, label: "Slightly" },
        { value: 3, label: "Moderately" },
        { value: 4, label: "Very" },
        { value: 5, label: "Extremely" }
      ];

  return (
    <div className="personality-assessment">
      <div className="assessment-header mb-6">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white mb-4"
        >
          ← Back
        </button>
        
        <div className="progress-container">
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-400 mt-2">
            Question {currentQuestionIndex + 1} of {allQuestions.length}
          </p>
        </div>
      </div>

      <div className="question-card bg-gray-800 rounded-lg p-8 mb-6">
        <h2 className="text-xl font-semibold text-white mb-6">
          {currentQuestion.text}
        </h2>
        
        <div className="answer-options space-y-3">
          {answerOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleResponse(option.value)}
              className={`w-full p-4 rounded-lg border transition-all ${
                responses[currentQuestion.id] === option.value
                  ? 'border-blue-500 bg-blue-900 text-white'
                  : 'border-gray-600 hover:border-gray-500 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="assessment-footer flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Previous
        </button>
        
        {responses[currentQuestion.id] !== undefined && !isLastQuestion && (
          <button
            onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonalityAssessment;